/**
 * SEO meta 集中管理：路由 → title/description/i18n key 映射 + hreflang/canonical 生成。
 *
 * 设计目标：
 * - SPA 运行时由 components/Seo.tsx 调用 setMeta() 动态写入 <head>。
 * - 构建期 prerender 复用同一份 ROUTE_META，把 meta 写进静态 HTML（见 SEO_CONTENT_PLAN.md §5）。
 * - hreflang / canonical 按 SEO_CONTENT_PLAN.md §4.2 规范：
 *     · 每个公开页注入 4 语言 hreflang + x-default(=en)
 *     · canonical 指向当前语言版本自身
 *
 * 站点基准 URL：生产 https://tsuvi.com。
 * 开发/预览环境由 dev server 的 host 决定，但 prerender 产物固定用生产域名
 * （搜索引擎只访问生产站点）。
 */

import { SUPPORTED_LANGS, DEFAULT_LANG, type Lang } from "../i18n";

/** 生产站点基准 URL（prerender 产物固定用此值）。 */
export const SITE_URL = "https://tsuvi.com";

/**
 * 站点语言 → Open Graph locale（Facebook 等社交平台的本地化展示）。
 * zh 为简体（zh.json 简体中文）。prerender.mjs 有同步副本（构建期无法 import TS）。
 */
export const OG_LOCALE: Record<Lang, string> = {
  en: "en_US",
  zh: "zh_CN",
  ja: "ja_JP",
  ko: "ko_KR",
};

/**
 * 路由 meta 配置表。
 * key = 去掉语言前缀的路径（落地页用 "" 表示 /:lang 根）。
 * titleKey / descKey 指向 locales 的 seo.* 命名空间，由 useTranslation 解析。
 */
export interface RouteMeta {
  /** 去掉语言前缀的路径，如 "/pricing"、""（落地页） */
  path: string;
  /** SEO title 的 i18n key（seo.<route>.title） */
  titleKey: string;
  /** meta description 的 i18n key（seo.<route>.description） */
  descKey: string;
  /** OG image 相对路径（可选，默认 /og-image.png） */
  ogImage?: string;
  /**
   * 动态路由前缀标记（如 /guides/:slug）。
   * true 时 Seo 组件只注入 hreflang/canonical，title/description 由页面组件自行设置
   * （因为内容来自 manifest，不在 i18n 字典里）。
   */
  dynamic?: boolean;
  /**
   * 仅英文内容标记（guides/use-cases/法律页 terms+privacy）。
   * true 时 hreflang 只发 en + x-default，不为 zh/ja/ko 生成会重定向的死链。
   */
  englishOnly?: boolean;
}

/** 公开页 meta 配置。枢纽页（/guides 等）与动态文章路由前缀在此声明。 */
export const ROUTE_META: Record<string, RouteMeta> = {
  // 落地页
  "": {
    path: "",
    titleKey: "seo.landing.title",
    descKey: "seo.landing.description",
  },
  "/pricing": {
    path: "/pricing",
    titleKey: "seo.pricing.title",
    descKey: "seo.pricing.description",
  },
  "/plaza": {
    path: "/plaza",
    titleKey: "seo.plaza.title",
    descKey: "seo.plaza.description",
  },
  // 法律文档与 blog/guides 一样仅英文（EnglishOnlyRoute 重定向，见 LegalPage）
  "/terms": {
    path: "/terms",
    titleKey: "seo.terms.title",
    descKey: "seo.terms.description",
    englishOnly: true,
  },
  "/privacy": {
    path: "/privacy",
    titleKey: "seo.privacy.title",
    descKey: "seo.privacy.description",
    englishOnly: true,
  },
  "/aup": {
    path: "/aup",
    titleKey: "seo.aup.title",
    descKey: "seo.aup.description",
    englishOnly: true,
  },
  "/guides": {
    path: "/guides",
    titleKey: "seo.guides.title",
    descKey: "seo.guides.description",
    englishOnly: true,
  },
  "/use-cases": {
    path: "/use-cases",
    titleKey: "seo.useCases.title",
    descKey: "seo.useCases.description",
    englishOnly: true,
  },
  "/faq": {
    path: "/faq",
    titleKey: "seo.faqPage.title",
    descKey: "seo.faqPage.description",
  },
};

/**
 * 动态文章路由前缀 → 其枢纽 ROUTE_META（用于 routeMetaFromPath 前缀匹配）。
 * 文章页（如 /guides/image-to-stl）继承枢纽的 englishOnly 等属性，但标记 dynamic，
 * 让 Seo 组件只管 hreflang/canonical，title/description 由页面注入。
 */
const DYNAMIC_PREFIXES: Record<string, string> = {
  "/guides/": "/guides",
  "/use-cases/": "/use-cases",
};

/**
 * 生成某个公开页的 hreflang + canonical 标签（HTML 字符串，供 prerender 注入）。
 * 运行时注入（components/Seo.tsx）则用对象形式逐个创建 <link>。
 */
export interface HreflangEntry {
  hreflang: string;
  href: string;
}

/**
 * 生成某路径的 hreflang 列表 + canonical URL。
 * @param path 去掉语言前缀的路径（如 "/pricing"、""）
 * @param englishOnly 仅英文内容（guides/use-cases）：只发 en + x-default，
 *                    不为 zh/ja/ko 生成会被 EnglishOnlyRoute 重定向的死链。
 * @returns hreflang 列表（含 x-default）+ canonical（当前语言版本）
 */
export function buildHreflangs(
  path: string,
  currentLang: Lang,
  englishOnly = false,
): {
  hreflangs: HreflangEntry[];
  canonical: string;
} {
  const langs = englishOnly ? [DEFAULT_LANG] : SUPPORTED_LANGS;
  const hreflangs: HreflangEntry[] = langs.map((lang) => ({
    hreflang: lang,
    href: `${SITE_URL}/${lang}${path}`,
  }));
  // x-default 固定指向默认语言（en）
  hreflangs.push({
    hreflang: "x-default",
    href: `${SITE_URL}/${DEFAULT_LANG}${path}`,
  });
  const canonical = `${SITE_URL}/${currentLang}${path}`;
  return { hreflangs, canonical };
}

/**
 * 根据当前 URL 推导路由 meta。
 * 从 pathname（如 "/en/pricing"、"/en/guides/image-to-stl"）去掉语言前缀后匹配：
 * - 精确匹配 ROUTE_META（枢纽页：/guides、/pricing 等）
 * - 前缀匹配 DYNAMIC_PREFIXES（文章页：/guides/:slug → 继承 /guides meta + dynamic 标记）
 * 未匹配返回 null（workbench 等登录页不注入 SEO meta）。
 */
export function routeMetaFromPath(pathname: string): RouteMeta | null {
  // 去掉语言前缀
  let stripped = pathname;
  for (const lang of SUPPORTED_LANGS) {
    const prefix = `/${lang}`;
    if (stripped === prefix) {
      stripped = "";
      break;
    }
    if (stripped.startsWith(`${prefix}/`)) {
      stripped = stripped.slice(prefix.length);
      break;
    }
  }
  // 精确匹配（枢纽页）
  const exact = ROUTE_META[stripped];
  if (exact) return exact;
  // 前缀匹配（动态文章页）：/guides/:slug → 继承 /guides 的 meta，标记 dynamic
  for (const [prefix, hubKey] of Object.entries(DYNAMIC_PREFIXES)) {
    if (stripped.startsWith(prefix)) {
      const hub = ROUTE_META[hubKey];
      if (hub) {
        // 返回带原 path（完整文章路径）+ dynamic 标记的副本
        return { ...hub, path: stripped, dynamic: true };
      }
    }
  }
  return null;
}
